import { AssetFormServiceInstance } from 'services/AssetFormService'
import { FormField } from 'types/form-fields'
import { formatAssetName } from 'utils/strings'

/**
 * CMS form `fieldName` may use entity keys (packshotUrl) or multipart keys (packshot).
 * Match either when deciding if a logical LP asset field is on the configured form.
 */
const LOGICAL_FIELD_FORM_ALIASES: Record<string, readonly string[]> = {
  name: ['name'],
  sku: ['sku'],
  packshot: ['packshot', 'packshotUrl'],
  packshotAlt: ['packshotAlt'],
  openedPackshot: ['openedPackshot', 'openedPackshotUrl'],
  openedPackshotAlt: ['openedPackshotAlt'],
  image: ['image', 'imageUrl'],
  largeImage: ['largeImage', 'largeImageUrl'],
  landingPageFile: ['landingPageFile', 'landingPageFileUrl'],
  activeImage: ['activeImage', 'activeImageUrl'],
  sourceGraphicFile: ['sourceGraphicFile', 'sourceGraphicFileUrl'],
  attributeAlt: ['attributeAlt'],
  attributeAltMobile: ['attributeAltMobile'],
  video: ['video', 'videoUrl'],
  videoTitle: ['videoTitle'],
  videoYoutubeId: ['videoYoutubeId'],
  videoUrl: ['videoUrl', 'video'],
  pdfFile: ['pdfFile', 'pdfFileUrl', 'pdf'],
  defaultDescription: ['defaultDescription'],
  descriptionForEuro: ['descriptionForEuro'],
  additionalText: ['additionalText'],
  bannerTitle: ['bannerTitle'],
  bannerTitleAccent: ['bannerTitleAccent'],
  bannerSubtitle: ['bannerSubtitle'],
  bannerDisclaimer: ['bannerDisclaimer']
}

const PACKSHOT_FORM_FIELD_NAMES = new Set([
  'sku',
  'packshot',
  'packshotUrl',
  'packshotAlt',
  'openedPackshot',
  'openedPackshotUrl',
  'openedPackshotAlt'
])

const PACKSHOT_LOGICAL_FIELDS = new Set([
  'sku',
  'packshot',
  'packshotAlt',
  'openedPackshot',
  'openedPackshotAlt'
])

function resolveFormFieldKey(field: FormField): string {
  return (field.name || field.field || '').trim()
}

function formMentionsAnyPackshotField(formFields: FormField[]): boolean {
  return formFields.some(f => PACKSHOT_FORM_FIELD_NAMES.has(resolveFormFieldKey(f)))
}

export function isLogicalFieldOnLandingPageAssetForm(
  logicalField: string,
  formFields: FormField[]
): boolean {
  if (!formFields.length) {
    return true
  }

  const aliases = LOGICAL_FIELD_FORM_ALIASES[logicalField] ?? [logicalField]

  if (formFields.some(f => aliases.includes(resolveFormFieldKey(f)))) {
    return true
  }

  if (
    PACKSHOT_LOGICAL_FIELDS.has(logicalField) &&
    !formMentionsAnyPackshotField(formFields)
  ) {
    return true
  }

  return false
}

/** Asset type allows the field (AssetFormService) and it is on the CMS form when one is configured. */
export function isLandingPageAssetFormFieldVisible(
  logicalField: string,
  assetTypeName: string,
  formFields: FormField[],
  brand?: string
): boolean {
  const formattedType = formatAssetName(assetTypeName)

  if (
    !AssetFormServiceInstance.isFieldAllowedInAssetType(
      formattedType,
      logicalField,
      brand
    )
  ) {
    return false
  }

  return isLogicalFieldOnLandingPageAssetForm(logicalField, formFields)
}

export function isPackshotLandingPageAssetType(
  assetTypeName: string,
  brand?: string
): boolean {
  const formattedType = formatAssetName(assetTypeName)

  return (
    isLandingPageAssetFormFieldVisible('packshot', assetTypeName, [], brand) ||
    isLandingPageAssetFormFieldVisible('openedPackshot', assetTypeName, [], brand) ||
    formattedType === 'packshots' ||
    formattedType === 'pack_shot'
  )
}

/** CMS form rows rendered elsewhere (file uploads, packshot section, etc.). */
const DEDICATED_LP_ASSET_FORM_FIELD_NAMES = new Set([
  'image',
  'imageUrl',
  'largeImage',
  'largeImageUrl',
  'landingPageFile',
  'landingPageFileUrl',
  'activeImage',
  'activeImageUrl',
  'sourceGraphicFile',
  'sourceGraphicFileUrl',
  'attributeAlt',
  'attributeAltMobile',
  'activeImageAlt',
  'video',
  'videoUrl',
  'videoTitle',
  'videoYoutubeId',
  'pdfFile',
  'pdfFileUrl',
  'sku',
  'packshot',
  'packshotUrl',
  'packshotAlt',
  'openedPackshot',
  'openedPackshotUrl',
  'openedPackshotAlt',
  'hotpoints',
  'hotpointsUrl',
  'hotpointsAlt',
  'hotpointsMobile',
  'hotpointsMobileUrl',
  'hotpointsMobileAlt',
  'hotpointsProperties',
  'hotpointsMobileProperties',
  'hotpointTemplate',
  'bannerTitle',
  'bannerTitleAccent',
  'bannerSubtitle',
  'bannerDisclaimer',
  'defaultDescription',
  'descriptionForEuro',
  'additionalText'
])

export function filterDedicatedLandingPageAssetFormFields(
  formFields: FormField[]
): FormField[] {
  return formFields.filter(
    f => !DEDICATED_LP_ASSET_FORM_FIELD_NAMES.has(resolveFormFieldKey(f))
  )
}

const LP_HOTPOINTS_FORM_FIELD_NAMES = new Set([
  'hotpoints',
  'hotpointsUrl',
  'hotpointsMobile',
  'hotpointsMobileUrl',
  'hotpointsProperties',
  'hotpointsMobileProperties',
  'hotpointsAlt',
  'hotpointsMobileAlt',
  'hotpointTemplate'
])

/** Dedicated LP hotpoints editor (not used on product forms). */
export function isLpHotpointsEditorVisible(
  assetTypeName: string,
  formFields: FormField[]
): boolean {
  if (formatAssetName(assetTypeName) === 'hotpoints') {
    return true
  }

  if (!formFields.length) {
    return false
  }

  return formFields.some(f =>
    LP_HOTPOINTS_FORM_FIELD_NAMES.has(resolveFormFieldKey(f))
  )
}
